import { supabase } from "@/lib/supabase";
import { processAndUploadImage } from "@/lib/image-processor";

export interface SellerApplication {
  id: string;
  user_id: string;
  current_step: number;
  status: string;
  business_name: string | null;
  seller_phone: string | null;
  primary_category: string | null;
  secondary_categories: string[] | null;
  seller_type: string | null;
  store_name: string | null;
  store_description: string | null;
  country: string | null;
  province: string | null;
  city: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  accepted_terms: boolean;
  terms_version: string | null;
  terms_accepted_at: string | null;
  submitted_at: string | null;
  created_at: string | null;
}

export interface VerificationDoc {
  id: string;
  document_type: "front" | "back";
  storage_path: string;
  status: string;
}

export const TERMS_VERSION = "1.0";

export async function loadApplication(
  userId: string
): Promise<SellerApplication | null> {
  const { data, error } = await supabase
    .from("seller_applications")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return (data as SellerApplication) ?? null;
}

export async function loadVerificationDocs(
  userId: string
): Promise<VerificationDoc[]> {
  const { data, error } = await supabase
    .from("seller_verification_documents")
    .select("id, document_type, storage_path, status")
    .eq("user_id", userId);
  if (error) throw error;
  return (data as VerificationDoc[]) ?? [];
}

export async function saveApplicationDraft(
  userId: string,
  fields: Partial<SellerApplication>
): Promise<SellerApplication> {
  const { data, error } = await supabase
    .from("seller_applications")
    .upsert({ user_id: userId, ...fields }, { onConflict: "user_id" })
    .select()
    .single();
  if (error) throw error;
  return data as SellerApplication;
}

export async function uploadVerificationDoc(
  userId: string,
  applicationId: string | null,
  documentType: "front" | "back",
  file: File
): Promise<VerificationDoc> {
  const result = await processAndUploadImage({
    file,
    bucket: "seller-verification",
    folder: userId,
    kind: "document",
    prefix: `cedula-${documentType}`,
  });
  const path = result.path;

  // Reemplaza el documento anterior del mismo tipo (frente/dorso).
  const { data: existing } = await supabase
    .from("seller_verification_documents")
    .select("id, storage_path")
    .eq("user_id", userId)
    .eq("document_type", documentType);

  if (existing && existing.length > 0) {
    await supabase.storage
      .from("seller-verification")
      .remove([existing[0].storage_path]);
    await supabase
      .from("seller_verification_documents")
      .delete()
      .eq("id", existing[0].id);
  }

  const { data, error: insertError } = await supabase
    .from("seller_verification_documents")
    .insert({
      user_id: userId,
      application_id: applicationId,
      document_type: documentType,
      storage_path: path,
      status: "pending",
    })
    .select()
    .single();
  if (insertError) throw insertError;
  return data as VerificationDoc;
}

export async function deleteVerificationDoc(
  doc: VerificationDoc
): Promise<void> {
  await supabase.storage.from("seller-verification").remove([doc.storage_path]);
  await supabase.from("seller_verification_documents").delete().eq("id", doc.id);
}

export async function submitApplication(applicationId: string): Promise<void> {
  const { error } = await supabase.rpc("submit_seller_application", {
    p_application_id: applicationId,
  });
  if (error) throw new Error(error.message);
}